/**
 * Geometry helpers for simple-structure HPR calculations.
 *
 * All Cesium HPR convention knowledge lives here. Client code must NOT
 * replicate these formulas — it should call these functions via the
 * viewer public API (or import them from the `earthbrain-viewer` package).
 */
// ── Unit helpers ────────────────────────────────────────────────────────────
function mmToM(mm) {
    return mm / 1000;
}
function _roundTo3(v) {
    const n = Number(v);
    return Number.isFinite(n) ? Number(n.toFixed(3)) : 0;
}
function _roundPointTo3(p) {
    return {
        x: _roundTo3(p.x),
        y: _roundTo3(p.y),
        z: _roundTo3(p.z),
    };
}
// ── Box Culvert (single-point) ──────────────────────────────────────────────
export function buildBoxCulvertFromAnchor(options) {
    const { P: edgeMidpoint, width_mm, height_mm, length_m } = options;
    const W = mmToM(width_mm);
    const H = mmToM(height_mm);
    const L = length_m;
    const center = {
        x: edgeMidpoint.x,
        y: edgeMidpoint.y - L / 2,
        z: edgeMidpoint.z + H / 2,
    };
    return {
        pivot: { ...edgeMidpoint },
        center,
        dimensions: { x: W, y: L, z: H },
        rotation: { x: 0, y: 0, z: 0 },
        meta: { anchorType: "edge-midpoint" },
    };
}
// ── Manhole (single-point) ──────────────────────────────────────────────────
export function buildManholeFromAnchor(options) {
    const { P: topCenter, diameter_mm, depth_m } = options;
    const D = mmToM(diameter_mm);
    const dimensions = { x: D, y: D, z: depth_m };
    const center = {
        x: topCenter.x,
        y: topCenter.y,
        z: topCenter.z - depth_m / 2,
    };
    return {
        pivot: { ...topCenter },
        center,
        dimensions,
        rotation: { x: 0, y: 0, z: 0 },
        meta: { anchorType: "top-center" },
    };
}
// ── Custom shape (single-point) ─────────────────────────────────────────────
/**
 * Build a custom box / sphere / cylinder from its bottom-nw anchor.
 * Center is offset from the anchor by +x/2 (east), -y/2 (south), +z/2 (up).
 * Dimensions are metres; the caller picks the shapeType (same envelope for all).
 */
export function buildCustomShapeFromAnchor(options) {
    const { P: bottomNw, dimensions } = options;
    const center = {
        x: bottomNw.x + dimensions.x / 2,
        y: bottomNw.y - dimensions.y / 2,
        z: bottomNw.z + dimensions.z / 2,
    };
    return {
        pivot: { ...bottomNw },
        center,
        dimensions: { ...dimensions },
        rotation: { x: 0, y: 0, z: 0 },
        meta: { anchorType: "bottom-nw" },
    };
}
// ── Pipe (two-point) ────────────────────────────────────────────────────────
/**
 * Build pipe (cylinder) parameters from two anchor points (inlet / outlet lower edges).
 *
 * Orientation derivation (Cesium HPR convention, roll=0):
 *   direction = normalize(P2 - P1) = (dx, dy, dz)
 *   pitch   = arccos(dz)            — angle from vertical (+Z) axis
 *   heading = atan2(dy, -dx)        — Cesium HPR maps cylinder Z to
 *             (-sinP·cosH, sinP·sinH, cosP)
 */
export function buildPipeFromTwoPoints(options) {
    const { P1, P2, diameter_mm } = options;
    const dx = P2.x - P1.x;
    const dy = P2.y - P1.y;
    const dz = P2.z - P1.z;
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const D = mmToM(diameter_mm);
    if (length < 1e-10) {
        return {
            pivot: { ...P1 },
            center: { ...P1 },
            dimensions: { x: D, y: D, z: D },
            rotation: { x: 0, y: 0, z: 0 },
            meta: { anchorType: "center" },
        };
    }
    const dirZ = dz / length;
    const dirX = dx / length;
    const dirY = dy / length;
    const sinP = Math.sqrt(dirX * dirX + dirY * dirY);
    const radius = D / 2;
    const centerOffset = sinP > 1e-10
        ? {
            x: (-dirZ * dirX * radius) / sinP,
            y: (-dirZ * dirY * radius) / sinP,
            z: sinP * radius,
        }
        : { x: 0, y: 0, z: 0 };
    const center = {
        x: (P1.x + P2.x) / 2 + centerOffset.x,
        y: (P1.y + P2.y) / 2 + centerOffset.y,
        z: (P1.z + P2.z) / 2 + centerOffset.z,
    };
    // Cesium HPR: cylinder Z-axis → (-sinP·cosH, sinP·sinH, cosP)
    let pitch = Math.acos(Math.max(-1, Math.min(1, dirZ)));
    let heading = Math.atan2(dirY, -dirX);
    let roll = 0;
    // Gimbal-lock avoidance: equivalent HPR (H+π, π-P, R+π)
    if (pitch > Math.PI / 2 + 1e-10) {
        pitch = Math.PI - pitch;
        heading = heading + Math.PI;
        roll = Math.PI;
    }
    const anchorType = sinP > 1e-10 ? "bottom-start" : "bottom-center";
    return {
        pivot: { ...P1 },
        center,
        dimensions: { x: D, y: D, z: length },
        rotation: { x: roll, y: pitch, z: heading },
        meta: { anchorType },
    };
}
// ── Retaining Wall (box from two top-edge points) ───────────────────────────
export function buildRetainingWallFromTwoPoints(options) {
    const { P1, P2, height_m, gradient_percent = 0, thickness_m = 0.5 } = options;
    const dx = P2.x - P1.x;
    const dy = P2.y - P1.y;
    const horizontalDist = Math.sqrt(dx * dx + dy * dy);
    const width = horizontalDist;
    const thickness = thickness_m;
    // Cesium HPR(H, 0, R): box X-axis → (cosH, -sinH, 0)
    const heading = horizontalDist > 1e-10 ? Math.atan2(-dy, dx) : 0;
    const roll = Math.atan(gradient_percent / 100);
    const slopeHeight = Math.abs(roll) > 1e-15 ? height_m / Math.cos(roll) : height_m;
    const y_hat_x = horizontalDist > 1e-10 ? -dy / horizontalDist : 0;
    const y_hat_y = horizontalDist > 1e-10 ? dx / horizontalDist : 0;
    // center = P1 - R·anchorOffset (anchorOffset for "top-start" = (-W/2, T/2, sH/2)).
    // Roll rotates the object Y/Z axes about the wall length axis (x̂), so the
    // thickness/height offsets must be applied in the rolled frame — otherwise
    // getAnchorLocal() (= center + R·offset) drifts off P1 when gradient ≠ 0:
    //   Δy = T/2·(cosθ-1) - sH/2·sinθ, Δz = T/2·sinθ + sH/2·(cosθ-1)
    const cosR = Math.cos(roll);
    const sinR = Math.sin(roll);
    // R·offset = (-W/2)·x̂ + (T/2·cosθ - sH/2·sinθ)·ŷ + (T/2·sinθ + sH/2·cosθ)·ẑ
    // P1 - (-W/2)·x̂ = XY midpoint, so only the ŷ/ẑ components remain below.
    const offsetAlongY = (thickness / 2) * cosR - (slopeHeight / 2) * sinR;
    const offsetAlongZ = (thickness / 2) * sinR + (slopeHeight / 2) * cosR;
    const center = {
        x: (P1.x + P2.x) / 2 - offsetAlongY * y_hat_x,
        y: (P1.y + P2.y) / 2 - offsetAlongY * y_hat_y,
        z: P1.z - offsetAlongZ,
    };
    const bottomZ = P1.z - height_m;
    return {
        pivot: { ...P1 },
        center,
        dimensions: { x: width, y: thickness, z: slopeHeight },
        rotation: { x: roll, y: 0, z: heading },
        meta: { anchorType: "top-start", bottomZ },
    };
}
// ── Inverse helpers (edit panels) ───────────────────────────────────────────
/**
 * Compute bottom Z from top-edge Z and vertical height.
 */
export function computeRetainingWallBottomZ(topZ, height_m) {
    return topZ - height_m;
}
/**
 * Compute pipe gradient and 3D length from two local-space points.
 */
export function computePipeGradientFromPoints(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dz = p2.z - p1.z;
    const hd = Math.sqrt(dx * dx + dy * dy);
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
    let gradientValue = 0;
    if (hd > 1e-10) {
        const gPct = (dz / hd) * 100;
        gradientValue = gPct !== 0 ? _roundTo3(100 / gPct) : 0;
    }
    return { gradientValue, length };
}
/** Pipe gradient percent (dz/horizontal*100) from two local-space points. */
export function pipeGradientPercentFromPoints(p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const horizontal = Math.sqrt(dx * dx + dy * dy);
    return horizontal > 1e-10 ? ((p2.z - p1.z) / horizontal) * 100 : 0;
}
/**
 * Rotate the horizontal (XY) component of segment P1→P2 about P1 by a
 * display-heading delta in degrees, keeping the vertical rise.
 * Convention-free: for both the pipe (atan2(dy,-dx)) and retaining-wall
 * (atan2(-dy,dx)) HPR headings, a +delta heading change equals rotating the
 * XY vector by -delta in standard math orientation.
 * @returns New P2 (P1 unchanged)
 */
export function rotateSegmentXYByHeadingDelta(p1, p2, deltaDeg) {
    const rad = (-deltaDeg * Math.PI) / 180;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return _roundPointTo3({
        x: p1.x + dx * Math.cos(rad) - dy * Math.sin(rad),
        y: p1.y + dx * Math.sin(rad) + dy * Math.cos(rad),
        z: p2.z,
    });
}
/**
 * Scale cylinder segments by 3D length and other segments by horizontal length, keeping P1.
 * Non-cylinder segments retain P2.z; degenerate segments or target lengths return P2 unchanged.
 */
export function scaleSegmentToLength(p1, p2, newLength, shapeType) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dz = p2.z - p1.z;
    const length = shapeType === "cylinder"
        ? Math.sqrt(dx * dx + dy * dy + dz * dz)
        : Math.sqrt(dx * dx + dy * dy);
    if (length < 1e-10 || !(newLength > 0))
        return { ...p2 };
    const k = newLength / length;
    return shapeType === "cylinder"
        ? {
            x: p1.x + dx * k,
            y: p1.y + dy * k,
            z: p1.z + dz * k,
        }
        : {
            x: p1.x + dx * k,
            y: p1.y + dy * k,
            z: p2.z,
        };
}
/**
 * Retaining-wall gradient percent from the HPR roll in degrees.
 * Inverse of the roll derivation in `buildRetainingWallFromTwoPoints`.
 */
export function wallGradientPercentFromRollDeg(rollDeg) {
    const rollRad = (rollDeg * Math.PI) / 180;
    return Number((Math.tan(rollRad) * 100).toFixed(2));
}
/**
 * Vertical wall height from the slope (box Z) height and gradient percent.
 * Inverse of the slope-height derivation in `buildRetainingWallFromTwoPoints`.
 */
export function verticalHeightFromSlopeHeight(slopeHeight, gradientPercent) {
    const roll = Math.atan((gradientPercent || 0) / 100);
    return _roundTo3(slopeHeight * Math.cos(roll));
}
/**
 * 勾配%の変更から新しい P2 を計算する（3D 長は維持、XY 方位は元の P1→P2 を保持）。
 * 戻り値はフル精度（mm 丸めなし）：結果はクォータニオン導出に使われるため、
 * 急勾配配管の微小な水平成分を mm に量子化すると再導出勾配が歪む
 * （例: 500% → 501%）。丸めは表示側（入力コンポーネント）の責務とする。
 */
export function computePipeP2FromGradient(p1, p2, gradientPercent, targetLength) {
    const alpha = Math.atan(gradientPercent / 100);
    const newDz = targetLength * Math.sin(alpha);
    const newHorizDist = targetLength * Math.cos(alpha);
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const hd = Math.sqrt(dx * dx + dy * dy);
    const bearingX = hd > 1e-10 ? dx / hd : 1;
    const bearingY = hd > 1e-10 ? dy / hd : 0;
    return {
        x: p1.x + bearingX * newHorizDist,
        y: p1.y + bearingY * newHorizDist,
        z: p1.z + newDz,
    };
}
//# sourceMappingURL=simple-structure-geometry.js.map